import { FocusEvent } from 'react';

import { datadogRum } from '@datadog/browser-rum';
import once from 'lodash/once';
import { ClaimFlowType } from 'types';

import { AutofillEvent } from 'chime-ui/components/TextField/TextField';

function buildIntegrationsObject(integrationsDecisions) {
  return integrationsDecisions?.shouldSendToFacebook
    ? {}
    : {
        integrations: {
          'Facebook Pixel': false,
          'Facebook Conversions API': false,
          'Facebook Offline Conversions': false,
        },
      };
}

function track(eventName, eventProperties, integrationsDecisions = {}) {
  const integrations = buildIntegrationsObject(integrationsDecisions);

  analytics.track(eventName, eventProperties, integrations);
}

// Fires when a PAv2 receiver non-member visits the Landing Page (First Page) of the PAv2 Transfer
export const trackLandingPageViewed = (
  amount,
  publicId,
  senderId,
  claimFlowType
) => {
  track(
    claimFlowType === ClaimFlowType.pay
      ? 'Landing Page Viewed'
      : 'P2P Request Payment Landing Page Viewed',
    {
      amount: amount,
      public_id: publicId,
      sender_id: senderId,
    }
  );
};

export const trackValuePropsViewed = (pageLocation, publicId, position) => {
  track('Value Props Viewed', {
    public_id: publicId,
    page_location: pageLocation,
    position: position,
  });
};

// Fires when a PAv2 receiver non-member visits the Return User Page (First Page) of the PAv2 Transfer
export const trackReturnUserPageViewed = (amount, publicId, senderId) => {
  track('Return User Page Viewed', {
    amount: amount,
    public_id: publicId,
    sender_id: senderId,
  });
};

// Fires when a PAv2 receiver non-member visits the unified Claim Info Page (Second Page) of the PAv2 Transfer
export const trackPayAnyoneClaimInfoPageViewed = publicId => {
  trackClaimInfoPageViewed(ClaimFlowType.pay, publicId);
};

// Fires when a PAv2 receiver non-member visits the unified Claim Info Page (Second Page) of the PAv2 Transfer
export const trackReceiveFromAnyoneClaimInfoPageViewed = publicId => {
  trackClaimInfoPageViewed(ClaimFlowType.receive, publicId);
};

// Fires when a PAv2 receiver non-member visits the unified Claim Info Page (Second Page) of the PAv2 Transfer
const trackClaimInfoPageViewed = (claimType, publicId) => {
  track(
    claimType === ClaimFlowType.pay
      ? 'Pay Anyone Claim Info Page Viewed'
      : 'P2P Request Payment Claim Info Page Viewed',
    {
      public_id: publicId,
    }
  );
};

// Fires when a PAv2 receiver non-member visits the Confirmation Page (Fourth Page) of the PAv2 Transfer
export const trackPayAnyoneConfirmationInfoPageViewed = publicId => {
  track('Pay Anyone Confirmation Page Viewed', {
    public_id: publicId,
  });
};

// Fires when a Receive From Anyone non-member visits the Confirmation Page of the PAv2 Transfer
export const trackReceiveFromAnyoneConfirmationInfoPageViewed = (
  publicId,
  label
) => {
  track('P2P Request Payment Confirmation Page Viewed', {
    public_id: publicId,
    label,
  });
};

// Fires whenever a receiver taps any CTA button in the Transfer flow,
// including checking boxes on 'Debit Card Info Page' for terms & conditions
export const trackCTATapped = (
  eventPrefix,
  label, // text on CTA, example: 'Instantly transfer for free', 'Next'
  pageLocation, // specific page, example: 'Landing Page' , 'Personal Info Page'
  uniqueId, // what the button is, example: 'pay_anyone_next_button'
  publicId,
  claimOption = 'N/A'
) => {
  track(
    `${eventPrefix} CTA Tapped`,
    {
      label: label,
      page_location: pageLocation,
      unique_id: uniqueId,
      public_id: publicId,
      claim_option: claimOption,
    },
    { shouldSendToFacebook: true }
  );
};

// Fires whenever a PAv2 receiver types in a field and focuses out of the field to the next
// field or taps the next available CTA button in the PAv2 Transfer flow
export const trackFieldFilled = (
  eventPrefix,
  fieldLabel, // what the field is, example: 'First name'
  pageLocation, // specific page, example: 'Personal Info Page'
  value, // what was typed in, example: 'Annie'
  publicId
) => {
  track(`${eventPrefix} Field Filled`, {
    field_label: fieldLabel,
    page_location: pageLocation,
    value: value,
    public_id: publicId,
  });
};

export const trackPayAnyoneTermsAccepted = (
  termsValue,
  pageLocation,
  publicId
) => {
  trackTermsAccepted(ClaimFlowType.pay, termsValue, pageLocation, publicId);
};

export const trackReceiveFromAnyoneTermsAccepted = (
  termsValue,
  pageLocation,
  publicId
) => {
  trackTermsAccepted(ClaimFlowType.receive, termsValue, pageLocation, publicId);
};

// Fires whenever a PAv2 receiver submits the debit card info form successfully
const trackTermsAccepted = (claimType, termsValue, pageLocation, publicId) => {
  track(
    claimType === ClaimFlowType.pay
      ? 'Pay Anyone Terms Accepted'
      : 'P2P Request Payment Terms Accepted',
    {
      terms_accepted: termsValue,
      page_location: pageLocation,
      public_id: publicId,
    }
  );
};

// Fires whenever any type of error/failure occurs in the PAv2 Transfer flow
export const trackPayAnyoneReceiverError = (
  pageLocation, // specific page, example: 'Personal Info Page'
  errorMessage, // what the error display to receiver is
  publicId
) => {
  const error = new Error('Pay Anyone Receiver Error');
  track('Pay Anyone Receiver Error', {
    page_location: pageLocation,
    error_message: errorMessage,
    error_type: 'frontend',
    public_id: publicId,
  });

  datadogRum.addError(error, {
    publicId,
    errorMessage,
    pageLocation,
  });
};

type EventType = FocusEvent & {
  detail: {
    autoFilled: boolean;
  };
};

export type InputFieldTrackingFunction = (
  event: FocusEvent | AutofillEvent,
  publicId?: string
) => void;

export const handleTrackingInputs = (
  fn: InputFieldTrackingFunction
): InputFieldTrackingFunction =>
  once((event: EventType, publicId: string) => {
    if (
      event.type === 'blur' ||
      (event.type === 'autofill' && event.detail.autoFilled === true)
    ) {
      fn(event, publicId);
    }
  });

// Fire segment track event for successful OCR Scan
export const trackSuccessfulOcrScan = (publicId, isPartialScan) => {
  datadogRum.addAction('OCR Successful Scan', { publicId, isPartialScan });

  track('OCR Scanned', {
    public_id: publicId,
    is_partial: isPartialScan,
  });
};

// Fire segment track event for OCR not supported
export const trackOcrNotTriggered = (publicId, reason) => {
  track('OCR Not Triggered', {
    public_id: publicId,
    reason,
  });
};

// Fire segment track event for OCR triggered
export const trackOcrTriggered = publicId => {
  datadogRum.addAction('OCR Triggered', { publicId });
  track('OCR Triggered', {
    public_id: publicId,
  });
};

// Fire segment track event for OCR Recognizer error
export const trackOcrRecognizerError = (publicId, reason) => {
  track('Recognizer Error Event', {
    public_id: publicId,
    reason,
  });
};

// Fire segment track event for P2PEnrollEmail accessed
export const trackP2PEnrollEmailLinkAccessed = (
  publicId,
  receiverIdentifier
) => {
  track('P2P Enroll Email Link Accessed Event', {
    public_id: publicId,
    receiver_identifier: receiverIdentifier,
  });
};

export const getAnonymousId = () => {
  return analytics?.user()?.anonymousId();
};
